/**
 * Whether a user may change a session. Mirrors the backend write guard
 * (session_permissions.require_session_write):
 *
 * - Archived sessions and sessions in an unfinished merge are read-only for everyone, admins
 *   included, because the merge owns them.
 * - Admins are otherwise unrestricted.
 * - Auditors never author. They review what others produced, so every write affordance is off
 *   for them, including validating, which is not their job either.
 * - Everyone else writes their own sessions and those at a site they belong to.
 *
 * Falls back to the badge site when site membership hasn't loaded, as the rest of the app does.
 */

import { isSessionMergeLocked } from './sessionMergeState';

export interface SessionWriteAccess {
  canEdit: boolean;
  /** True when the viewer may read but not change this session. */
  isReadOnly: boolean;
}

export interface SessionWriteAccessUser {
  alias: string;
  isAdmin: boolean;
  isAuditor: boolean;
  sites: string[];
  siteCode: string;
}

interface SessionWriteAccessSource {
  created_by: string;
  group_id?: string | null;
  status: string;
  metadata?: Record<string, any> | null;
}

const EDITABLE: SessionWriteAccess = { canEdit: true, isReadOnly: false };
const READ_ONLY: SessionWriteAccess = { canEdit: false, isReadOnly: true };

export const userToSessionWriteAccess =
  (user: SessionWriteAccessUser) =>
  (session?: SessionWriteAccessSource | null): SessionWriteAccess => {
    if (session && isSessionMergeLocked(session)) return READ_ONLY;
    if (user.isAdmin) return EDITABLE;
    if (user.isAuditor) return READ_ONLY;
    // No session yet (the new-session screen), so nothing to be read-only about.
    if (!session || session.created_by === user.alias) return EDITABLE;

    const userSites = user.sites.length > 0 ? user.sites : user.siteCode ? [user.siteCode] : [];
    const isAtUserSite = !!session.group_id && userSites.includes(session.group_id);

    return isAtUserSite ? EDITABLE : READ_ONLY;
  };
